import { Link, useNavigate, useLocation } from "react-router-dom";
import { MapPin, Menu, X, LogOut, LayoutDashboard } from "lucide-react";
import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";

export const Navbar = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  const links = [
    { to: "/diretorio", label: "Diretório" },
    { to: "/ofertas-trabalho", label: "Ofertas de Trabalho" },
    { to: "/alugueis", label: "Aluguéis" },
    { to: "/propagandas", label: "Propagandas" },
    { to: "/reclame-aqui", label: "Reclame Aqui" },
    { to: "/contato", label: "Fale Conosco" },
  ];

  const onLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <header className="sticky top-0 z-40 bg-[#fafaf8]/85 backdrop-blur-md border-b border-[#e7e5e4]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-2">
          <span className="w-9 h-9 rounded-xl bg-[#1b5e3f] flex items-center justify-center">
            <MapPin className="w-5 h-5 text-white" strokeWidth={2} />
          </span>
          <span className="font-head text-xl font-bold text-[#1c1917]">
            Brasileiros<span className="text-[#f59e0b]">.PT</span>
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          {links.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.label.toLowerCase()}`}
              className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
                location.pathname === l.to ? "text-[#1b5e3f] bg-[#e8f0ec]" : "text-[#78716c] hover:text-[#1c1917] hover:bg-[#e7e5e4]/50"
              }`}
            >
              {l.label}
            </Link>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          {user ? (
            <>
              <Button data-testid="nav-dashboard-btn" variant="ghost" onClick={() => navigate("/dashboard")} className="rounded-xl text-[#1c1917]">
                <LayoutDashboard className="w-4 h-4 mr-2" /> Painel
              </Button>
              <Button data-testid="nav-logout-btn" onClick={onLogout} className="rounded-xl bg-[#1b5e3f] hover:bg-[#13422c]">
                <LogOut className="w-4 h-4 mr-2" /> Sair
              </Button>
            </>
          ) : (
            <>
              <Button data-testid="nav-login-btn" variant="ghost" onClick={() => navigate("/login")} className="rounded-xl text-[#1c1917]">Entrar</Button>
              <Button data-testid="nav-register-btn" onClick={() => navigate("/login?mode=register")} className="rounded-xl bg-[#1b5e3f] hover:bg-[#13422c]">Criar conta</Button>
            </>
          )}
        </div>

        <button data-testid="nav-mobile-toggle" className="md:hidden p-2" onClick={() => setOpen(!open)}>
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {open && (
        <div className="md:hidden border-t border-[#e7e5e4] bg-[#fafaf8] px-4 py-4 space-y-1">
          {links.map((l) => (
            <Link key={l.to} to={l.to} onClick={() => setOpen(false)} className="block px-4 py-3 rounded-xl text-[#1c1917] hover:bg-[#e8f0ec]">{l.label}</Link>
          ))}
          <div className="pt-2 flex flex-col gap-2">
            {user ? (
              <>
                <Button onClick={() => { setOpen(false); navigate("/dashboard"); }} className="rounded-xl bg-[#1b5e3f] hover:bg-[#13422c] w-full">Painel</Button>
                <Button variant="outline" onClick={() => { setOpen(false); onLogout(); }} className="rounded-xl w-full">Sair</Button>
              </>
            ) : (
              <>
                <Button onClick={() => { setOpen(false); navigate("/login"); }} variant="outline" className="rounded-xl w-full">Entrar</Button>
                <Button onClick={() => { setOpen(false); navigate("/login?mode=register"); }} className="rounded-xl bg-[#1b5e3f] hover:bg-[#13422c] w-full">Criar conta</Button>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
};

export const Footer = () => (
  <footer className="border-t border-[#e7e5e4] bg-white mt-20">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 flex flex-col sm:flex-row items-center justify-between gap-4">
      <div className="flex items-center gap-2">
        <span className="w-8 h-8 rounded-lg bg-[#1b5e3f] flex items-center justify-center">
          <MapPin className="w-4 h-4 text-white" />
        </span>
        <span className="font-head font-bold text-[#1c1917]">Brasileiros<span className="text-[#f59e0b]">.PT</span></span>
      </div>
      <div className="flex items-center gap-5 text-sm">
        <Link to="/contato" className="text-[#78716c] hover:text-[#1b5e3f]">Fale Conosco</Link>
        <Link to="/privacidade" data-testid="footer-privacy-link" className="text-[#78716c] hover:text-[#1b5e3f]">Política de Privacidade</Link>
        <Link to="/termos" data-testid="footer-terms-link" className="text-[#78716c] hover:text-[#1b5e3f]">Termos de Serviço</Link>
      </div>
      <p className="text-xs text-[#78716c]">© Brasileiros.PT · Lisboa, Portugal</p>
    </div>
  </footer>
);
